import React, { useState, useEffect } from 'react';
import './App.css';
import ProductCard from './components/ProductCard';
import Modal from './components/Modal';
import WarrantyContent from './components/WarrantyContent';
import Footer from './components/Footer';
import Loading from './components/Loading';
import { products } from './utils/productData';
import { readExcelFile } from './utils/excelReader';
import { arabicProductNames } from './utils/arabicNames';
import { getCacheBustedUrl } from './utils/cacheBuster';

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [arabicNames, setArabicNames] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadExcelData = async () => {
      try {
        setIsLoading(true);
        const data = await readExcelFile();
        
        // Start with default Arabic names
        const namesMap = { ...arabicProductNames };
        
        // Process product names from Excel (second sheet) - override defaults if found
        if (data.productNames && data.productNames.length > 0) {
          // Try to find header row or start from first row
          let startRow = 0;
          for (let i = 0; i < Math.min(3, data.productNames.length); i++) {
            const row = data.productNames[i];
            if (row && row.length >= 2) {
              const firstCell = row[0]?.toString().trim().toLowerCase();
              // If this looks like a header, skip it
              if (firstCell && (firstCell.includes('folder') || firstCell.includes('product') || firstCell.includes('اسم'))) {
                startRow = i + 1;
                break;
              }
            }
          }
          
          // Process rows starting from startRow
          for (let i = startRow; i < data.productNames.length; i++) {
            const row = data.productNames[i];
            if (row && Array.isArray(row) && row.length >= 2) {
              const folderName = row[0]?.toString().trim();
              const arabicName = row[1]?.toString().trim();
              if (folderName && arabicName) {
                namesMap[folderName] = arabicName;
              }
            }
          }
        }
        setArabicNames(namesMap);
      } catch (error) {
        console.error('Error loading data:', error);
        // Use default names if Excel loading fails
        setArabicNames(arabicProductNames);
      } finally {
        // Add small delay to ensure smooth transition
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    };

    loadExcelData();
  }, []);

  const handleShowPopup = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="App">
      <main className="main-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-logo-wrapper">
              <img 
                src={getCacheBustedUrl("/Landing Page Data/Braun Logo.png")} 
                alt="Braun Logo" 
                className="hero-logo"
              />
            </div>
            <h1 className="hero-title">مرحباً بك في عالم الجودة والابتكار</h1>
            <p className="hero-subtitle">اكتشف مجموعتنا الواسعة من المنتجات عالية الجودة</p>
            <div className="hero-buttons">
              <button className="hero-warranty-button" onClick={handleShowPopup}>
                <span className="button-icon">📄</span>
                معلومات الضمان
              </button>
              <button 
                className="hero-products-button" 
                onClick={() => {
                  document.querySelector('.products-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span className="button-icon">📦</span>
                الوصول السريع للمنتجات
              </button>
            </div>
          </div>
          <div className="hero-products-slider-inline">
            <div className="products-scroll-container">
              <div className="products-scroll-content">
                {/* Create 4 copies for absolutely seamless scroll */}
                {[...Array(4)].map((_, copyIndex) => (
                  <React.Fragment key={`copy-${copyIndex}`}>
                    {products.map((product, index) => (
                      <div key={`${copyIndex}-${index}`} className="product-name-element">
                        {arabicNames[product.folder] || product.folder}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="products-section">
          <div className="section-header-wrapper">
            <h2 className="section-title">منتجاتنا</h2>
            <p className="section-subtitle">اختر منتجك لعرض دليل الاستخدام</p>
          </div>
          <div className="products-grid">
            {products.map((product, index) => (
              <ProductCard
                key={index}
                product={product}
                arabicName={arabicNames[product.folder]}
              />
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact-section">
          <div className="contact-container">
            <p className="contact-text">
              للاستفسارات و الشكاوى الرجاء التواصل مع الرقم المجاني:{' '}
              <a href="tel:1800400444" className="contact-phone" dir="ltr">1800 400 444</a>
            </p>
          </div>
        </section>
      </main>

      <Footer />

      {/* Warranty Modal */}
      <Modal
        isOpen={showPopup}
        onClose={handleClosePopup}
        title="معلومات الضمان"
      >
        <WarrantyContent />
      </Modal>
    </div>
  );
}

export default App;

